// Port of Flutter's curtainScreen.dart (DeviceID 51).
//
// The Lottie curtain animation over three round buttons: Open · Stop · Close.
// The JSON files are the ones from the Flutter app, copied to
// public/assets/animated/json/ and played with lottie-web.
//
// Commands are UPPERCASE with no prefix — OPENCURTAIN / STOPCURTAIN /
// CLOSECURTAIN. (An older React doc had these as lowercase 'cs…' variants; the
// Dart is the authority and it sends these.)
//
// ── A press is a TIMED RUN, not a state change ─────────────────────────────
// The motor is momentary: it is energised, runs for the configured seconds, and
// is switched off again. `core/hooks/useCurtain` owns that clock — this screen
// only draws it. The three rules (reverse on the opposite direction, ignore a
// repeat of the same one) are in `core/devices/curtainRunner`, shared with the
// desktop screen and the grid card so all three cannot disagree about one motor.
//
// ⚠️ THE ANIMATION IS DRIVEN BY THE RUN, NOT BY THE TAP. It used to play a fixed
// 8 s open / 7 s close on any tap of the corresponding button, which tracked
// nothing: it kept sweeping after a Stop, and on a 2-second curtain it was still
// opening long after the motor had finished. It now runs for exactly as long as
// the motor does and freezes where the motor stopped, which is the only reading
// of "where is the curtain" this screen can honestly offer — there is no
// position feedback from the hardware.

import React, { useEffect, useRef, useState } from 'react';
import { useContentHeight } from '../../components/MobileShell';
import { Box, Typography, useTheme, LinearProgress, IconButton } from '@mui/material';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import PauseIcon from '@mui/icons-material/Pause';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TimerIcon from '@mui/icons-material/Timer';
import { useNavigate } from 'react-router-dom';
import DeviceAppBar from './components/DeviceAppBar';
import useLongPress from '../../components/useLongPress';
import useCurtain from '../../../../core/hooks/useCurtain';
import useCurtainMenu from '../../../shared/controls/CurtainActions';
import CurtainTimesDialog from '../../../shared/CurtainTimesDialog';
import { getSchedules } from '../../../../core/api/api';

const OPEN_JSON = '/assets/animated/json/CurtainOpen.json';
const CLOSE_JSON = '/assets/animated/json/CurtainClose.json';

export default function CurtainScreen({ device }) {
  const theme = useTheme();
  const contentH = useContentHeight();
  const c = theme.sli;
  const navigate = useNavigate();
  const uno = device?.UniqueNumber;

  const curtain = useCurtain(device);
  // Reverse open / close — owner only, and the DEVICE does the swap. See
  // core/devices/curtainReverse.
  const menu = useCurtainMenu(device);
  const holderRef = useRef(null);
  const animRef = useRef(null);
  // Which clip is loaded. Held separately from the run because the artwork has
  // to STAY on screen after the motor stops — going back to a default would
  // throw away the only indication of which way it last went.
  const [clip, setClip] = useState('close');
  const [scheduled, setScheduled] = useState(false);
  const [editTimes, setEditTimes] = useState(false);

  const geom = (() => {
    // ⚠️ THE CONTENT AREA, NOT THE WINDOW. The window also holds the identity
    // strip and the company footer, which this screen does not get — sizing
    // from it overflows by exactly their height, and the overflow is clipped
    // rather than scrolled. See ui/mobile/components/MobileShell.
    const availH = contentH - 64;
    const panelWidth = Math.min(window.innerWidth, 560);
    const animSize = Math.min(460, Math.max(160, Math.min(panelWidth * 0.8, availH * 0.55)));
    return {
      panelWidth,
      animSize,
      btnPad: Math.min(34, Math.max(20, animSize * 0.075)),
      btnIcon: Math.min(44, Math.max(28, animSize * 0.1)),
    };
  })();

  // Load (or swap) the animation whenever the direction flips.
  //
  // lottie-web is ~80 kB and this is the only screen that needs it, so it is
  // imported on demand rather than bundled into the main chunk that every user
  // downloads at startup.
  useEffect(() => {
    const holder = holderRef.current;
    if (!holder) return undefined;
    let cancelled = false;

    if (animRef.current) { animRef.current.destroy(); animRef.current = null; }

    import('lottie-web').then(({ default: lottie }) => {
      if (cancelled || !holderRef.current) return;
      animRef.current = lottie.loadAnimation({
        container: holderRef.current,
        renderer: 'svg',
        loop: false,
        autoplay: false,
        path: clip === 'open' ? OPEN_JSON : CLOSE_JSON,
      });
    }).catch(() => { /* animation is decorative — the buttons still work */ });

    return () => {
      cancelled = true;
      if (animRef.current) { animRef.current.destroy(); animRef.current = null; }
    };
  }, [clip]);

  // Swap the clip when a run starts. Keyed on startsAt so a second run in the
  // same direction reloads and replays rather than sitting at the last frame.
  useEffect(() => {
    if (curtain.state === 'opening') setClip('open');
    else if (curtain.state === 'closing') setClip('close');
  }, [curtain.state, curtain.startsAt]);

  // Play at the speed of the actual run, and freeze the moment it ends.
  //
  // The clip is stretched to the configured seconds — `setSpeed` is a
  // multiplier, so a 4-second clip over a 2-second run plays at 2×. A curtain
  // set to 20 s therefore crawls, which is right: it is showing a 20-second
  // motor.
  useEffect(() => {
    const anim = animRef.current;
    if (!anim) return;
    const seconds = curtain.state === 'opening'
      ? curtain.times.openSeconds
      : curtain.times.closeSeconds;
    if (curtain.moving) {
      const dur = anim.getDuration() || seconds;
      anim.goToAndStop(0, true);
      anim.setSpeed(seconds > 0 ? dur / seconds : 1);
      anim.play();
    } else {
      // Freeze where it got to — a Stop half way leaves the curtain half open,
      // and snapping the artwork to either end would claim otherwise.
      anim.pause();
    }
  }, [curtain.moving, curtain.state, curtain.times, clip]);

  // Clock badge — the Dart checks outputs 1..4 and shows the badge if any is set.
  useEffect(() => {
    if (!uno) return undefined;
    let alive = true;
    Promise.all([1, 2, 3, 4].map((i) => getSchedules(uno, i)
      .then((r) => (Array.isArray(r.data) ? r.data.length > 0 : false))
      .catch(() => false)))
      .then((flags) => { if (alive) setScheduled(flags.some(Boolean)); });
    return () => { alive = false; };
  }, [uno]);

  const bodyPress = useLongPress(
    () => navigate(`/schedule/${uno}/1`, { state: { outputName: device?.DeviceName, device } }),
    null,
  );

  // `active` fills the button while its own direction runs; `dim` is the
  // disabled look for a press that would do nothing. See curtainRunner rule 3 —
  // repeating a direction is a STOP at the device, so the button must not send.
  const controlButton = (label, icon, onTap, { active = false, dim = false } = {}) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box
        onClick={dim ? undefined : onTap}
        sx={{
          bgcolor: active ? c.text : c.button,
          borderRadius: '50%',
          p: `${geom.btnPad}px`,
          display: 'flex',
          cursor: dim ? 'default' : 'pointer',
          opacity: dim ? 0.45 : 1,
          transition: 'background-color .15s, opacity .15s',
          boxShadow: active ? `0 0 0 4px ${c.button}66` : `0 6px 12px ${c.text}26`,
        }}
      >
        {React.cloneElement(icon, {
          sx: { fontSize: geom.btnIcon, color: active ? c.back : c.text },
        })}
      </Box>
      <Box sx={{ height: 8 }} />
      <Typography sx={{ fontWeight: 600, color: c.text }}>{label}</Typography>
    </Box>
  );

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', bgcolor: c.back, display: 'flex', flexDirection: 'column' }}>
      <DeviceAppBar
        device={device}
        extraItems={menu.items.map((it) => ({ icon: it.icon, title: it.label, onClick: it.onClick }))}
      />

      <Box {...bodyPress} sx={{ flex: 1, position: 'relative', display: 'flex', justifyContent: 'center' }}>
        {scheduled && (
          <ScheduleIcon sx={{
            position: 'absolute', top: 8, left: 8, fontSize: 30, color: c.button,
          }}
          />
        )}

        {/* The run time is a per-device setting an installer sets once, so it
            lives behind an icon rather than on the control surface. */}
        <IconButton
          onClick={() => setEditTimes(true)}
          sx={{ position: 'absolute', top: 4, right: 4, color: c.button }}
        >
          <TimerIcon />
        </IconButton>

        <Box sx={{
          maxWidth: geom.panelWidth,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
        >
          <Box ref={holderRef} sx={{ width: geom.animSize, maxWidth: '100%' }} />

          {/* The countdown. Reserved height, so the buttons do not jump down
              the screen the instant somebody presses one. */}
          <Box sx={{ width: '80%', mt: 2, minHeight: 34 }}>
            {curtain.moving && (
              <>
                <LinearProgress
                  variant="determinate"
                  value={curtain.progress}
                  sx={{ height: 6, borderRadius: 3 }}
                />
                <Typography
                  sx={{
                    mt: 0.5, textAlign: 'center', color: c.text, fontWeight: 600, fontSize: 14,
                  }}
                >
                  {`${curtain.opening ? 'Opening' : 'Closing'} — ${curtain.remainingSeconds}s`}
                </Typography>
              </>
            )}
          </Box>

          <Box sx={{ height: 16 }} />

          <Box sx={{
            display: 'flex', justifyContent: 'space-evenly', width: '100%',
          }}
          >
            {controlButton(curtain.labels.open, <KeyboardArrowLeftRoundedIcon />, curtain.open, {
              active: curtain.opening, dim: curtain.opening,
            })}
            {controlButton(curtain.labels.stop, <PauseIcon />, curtain.stop)}
            {controlButton(curtain.labels.close, <KeyboardArrowRightRoundedIcon />, curtain.close, {
              active: curtain.closing, dim: curtain.closing,
            })}
          </Box>
        </Box>
      </Box>

      <CurtainTimesDialog
        open={editTimes}
        onClose={() => setEditTimes(false)}
        curtain={curtain}
        deviceName={device?.DeviceName}
        reversed={menu.reversed}
      />
    </Box>
  );
}
